import React, { useEffect, useId, useState } from 'react';
// AdaptivePanel is the wrapper primitive that ADR-0032 / ADR-0050 point
// callers to. It owns the bare Modal import here.
// eslint-disable-next-line no-restricted-imports
import { Animated, Keyboard, Modal, Platform, Pressable, ScrollView, StyleSheet, View } from 'react-native';
import { Sheet, Text, Theme, YStack, useMedia, useThemeName } from 'tamagui';
import { Portal } from '@tamagui/portal';
import { useSafeAreaInsets } from 'react-native-safe-area-context';
import { colors } from '../theme/colors';
import { dropShadow } from '../theme/shadowStyles';
import { useThemeColors } from '../hooks/useThemeColors';
import { useOverlayA11y } from '../hooks/useOverlayA11y';
import { pushEscapeHandler } from '../utils/escapeStack';

/**
 * Centralised Esc handler per ADR-0077. Every presentation
 * (Sheet/Modal/Sidebar) calls this so keyboard users dismiss the
 * panel the same way regardless of mode. Uses the shared escape
 * stack so a single Esc keypress dismisses only the topmost open
 * panel, not every overlay in the stack. Native modals already
 * forward Esc via onRequestClose; the stack helper no-ops there.
 */
function useEscapeToClose(open: boolean, onClose: () => void) {
  useEffect(() => {
    if (!open) return;
    return pushEscapeHandler(onClose);
  }, [open, onClose]);
}

const SIDEBAR_WIDTH = 320;
const SIDEBAR_WIDTH_LG = 360;
const MODAL_MAX_WIDTH = 480;
// Stable references — passing inline literals would create new arrays on
// every render, which causes Tamagui's `Sheet` to re-run its snap logic and
// (with `dismissOnSnapToBottom`) collapse the sheet on every keystroke.
const DEFAULT_SNAP_POINTS: number[] = [60];
// RNW renders a vertical ScrollView with `overflow-x: hidden`, which clips the
// browser :focus-visible outline on any control sitting flush against the
// body's left/right edge (toggles, inline links — see ConsentSheet). We widen
// the scroll viewport by this gutter on each side and pull it back with a
// matching negative margin, so the clip box gains room for the ring while the
// content stays visually aligned with the header/footer (which render outside
// the ScrollView). See ADR-0077 (WCAG 2.4.11 — focus not obscured).
const FOCUS_RING_GUTTER = 8;
// Sizing for the non-fit PanelLayout (header / scrolling body / footer).
//
// `flexBasis: 'auto'` is load-bearing — do not collapse this back to `flex: 1`.
// `flex: 1` expands to `flex-basis: 0`, which sizes the layout's hypothetical
// height to zero. In a parent with a DEFINITE height (sidebar, percent-snap
// sheet) that is harmless: flex-grow claims the free space back. But the
// desktop modal card is content-sized — `maxHeight: '85%'` with no height —
// so there is no free space to distribute, the card collapses to its padding,
// and the header/body/footer spill out of the card as loose text (the panel
// looked "broken" rather than merely mis-sized).
//
// With an auto basis the layout hugs its content, still grows to fill a
// definite-height parent, and — because `flexShrink: 1` and `minHeight: 0`
// survive — still shrinks below its content when the 85% clamp bites, which
// is what bounds the inner ScrollView so it scrolls instead of overflowing.
const FILL_AND_SCROLL = {
  flexGrow: 1,
  flexShrink: 1,
  flexBasis: 'auto',
  minHeight: 0,
} as const;

export interface AdaptivePanelProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  children: React.ReactNode;
  /** How to present the panel. Default: 'adaptive'. */
  mode?: 'adaptive' | 'modal' | 'sidebar' | undefined;
  /** Plain-text title fallback. Ignored if `header` is provided. */
  title?: string | undefined;
  /** Header slot rendered above the scrollable body. Preferred over `title`. */
  header?: React.ReactNode | undefined;
  /** Footer slot pinned below the scrollable body (sticky action row). */
  footer?: React.ReactNode | undefined;
  /** Snap points for the sheet (phone). Default: [60]. Pass a stable
   *  reference (module-level constant) to avoid re-snap-thrash on
   *  parent re-renders. Ignored when `snapPointsMode === 'fit'`. */
  snapPoints?: number[] | undefined;
  /** How the phone sheet sizes itself. Default `'percent'` honours
   *  `snapPoints` as viewport percentages — the original behaviour.
   *  `'fit'` sizes the sheet (and drops the internal scroll wrapper on
   *  the body) so a short panel renders as one unified surface instead
   *  of a fixed-height frame with a separately-scrolling content area.
   *  Use `'fit'` only for panels whose content is guaranteed to fit on
   *  the smallest target viewport — overflow does not scroll. Only
   *  affects the phone sheet: desktop modal and sidebar always keep
   *  the bounded ScrollView layout because they have a defined height. */
  snapPointsMode?: 'percent' | 'fit' | undefined;
  /** When true, swipe-down can't dismiss the sheet on phone. Use for
   *  editor sheets where a keyboard-induced layout shift would otherwise
   *  collapse the sheet to the bottom snap point and dismiss it. Default
   *  false. */
  disableDragDismiss?: boolean | undefined;
}

/**
 * Unified overlay panel that adapts to viewport per ADR-0032.
 *
 * - Phone: always renders a bottom sheet (all modes)
 * - Desktop `adaptive`/`sidebar`: right sidebar with frosted glass
 * - Desktop `modal`: centered modal (max 480px)
 *
 * Layout: [header] | [scrollable children] | [footer]. The footer is rendered
 * outside the ScrollView so action rows stay pinned. Use `EntityPreview` in
 * `header` to show entity context consistently across drawers.
 *
 * `snapPointsMode='fit'` is a phone-sheet-only opt-in: it lets the sheet
 * size to its content and drops the internal ScrollView so the whole surface
 * scrolls as one unit. Desktop modal and sidebar ignore it — they have
 * bounded heights and need the ScrollView to recover from overflow.
 */
export function AdaptivePanel({
  open,
  onOpenChange,
  children,
  mode = 'adaptive',
  title,
  header,
  footer,
  snapPoints = DEFAULT_SNAP_POINTS,
  snapPointsMode = 'percent',
  disableDragDismiss = false,
}: AdaptivePanelProps) {
  const media = useMedia();
  const isPhone = !media.md;
  // One id per AdaptivePanel instance — paired with the header slot
  // so screen readers announce the panel title as the dialog name
  // (ADR-0077 aria-labelledby). Only forwarded to useOverlayA11y
  // when an element with that id will actually render — otherwise
  // the dialog ends up with a dangling aria-labelledby pointing at
  // nothing.
  const generatedLabelId = useId();
  const hasHeader = title !== undefined || header !== undefined;
  const labelId = hasHeader ? generatedLabelId : undefined;

  // Esc dismisses regardless of mode (lifted out of ModalPresentation
  // per ADR-0077). Native handles its own dismissal — hook no-ops there.
  const handleClose = React.useCallback(() => onOpenChange(false), [onOpenChange]);
  useEscapeToClose(open, handleClose);

  // Fit mode only applies to the phone sheet — desktop modal and
  // sidebar both have bounded heights (maxHeight: '85%' / full viewport)
  // and would silently clip overflowing content if we stripped the
  // ScrollView. So they get a non-fit PanelLayout regardless of the
  // caller's snapPointsMode.
  const desktopLayout = (
    <PanelLayout title={title} header={header} footer={footer} labelId={labelId} fit={false}>
      {children}
    </PanelLayout>
  );

  if (isPhone) {
    return (
      <SheetPresentation
        open={open}
        onOpenChange={onOpenChange}
        snapPoints={snapPoints}
        snapPointsMode={snapPointsMode}
        disableDragDismiss={disableDragDismiss}
        labelId={labelId}
      >
        <PanelLayout title={title} header={header} footer={footer} labelId={labelId} fit={snapPointsMode === 'fit'}>
          {children}
        </PanelLayout>
      </SheetPresentation>
    );
  }

  if (mode === 'modal') {
    return (
      <ModalPresentation open={open} onOpenChange={onOpenChange} labelId={labelId}>
        {desktopLayout}
      </ModalPresentation>
    );
  }

  return (
    <SidebarPresentation open={open} onOpenChange={onOpenChange} isLg={!!media.lg} labelId={labelId}>
      {desktopLayout}
    </SidebarPresentation>
  );
}

// ---------- Layout (header / scroll body / footer) ----------

function PanelLayout({
  title,
  header,
  footer,
  children,
  labelId,
  fit,
}: {
  title?: string | undefined;
  header?: React.ReactNode | undefined;
  footer?: React.ReactNode | undefined;
  children: React.ReactNode;
  labelId?: string | undefined;
  /** When true the body renders without the flex+ScrollView wrapper so
   *  the whole surface (a fit-mode Sheet, or a content-hugging Modal)
   *  scrolls as one unit. Caller guarantees content fits the viewport. */
  fit: boolean;
}) {
  // Only the single-line `title` form is a genuine heading. An arbitrary
  // `header` node often carries a title PLUS a body paragraph (consent,
  // entity previews) — see ConsentSheet / AddEntityChoicePanel — so it is
  // NOT marked role="heading" (that would announce the body text as
  // heading content and pollute the screen-reader heading rotor).
  const usingTitleText = !header && !!title;
  const resolvedHeader =
    header ?? (title ? <Text fontSize="$6" fontWeight="700">{title}</Text> : null);

  // Slight slide-up on enter so the content feels "anchored" to the user's
  // tap. The sheet/modal chrome already animates around us; this keeps the
  // inner contents from snapping into place out of sync.
  return (
    <YStack
      {...(fit ? null : FILL_AND_SCROLL)}
      gap="$3"
      transition="quick"
      enterStyle={{ opacity: 0, y: 8 }}
    >
      {resolvedHeader ? (
        // labelId pairs the header with the parent's aria-labelledby so
        // screen readers announce the panel's title as its name. role=
        // "heading" needs aria-level (Lighthouse ARIA audit); panel titles
        // sit below the page/dialog title, so level 2 — but only when the
        // header is the single-line title (see usingTitleText above).
        <YStack
          {...(usingTitleText ? { accessibilityRole: 'header' as const, 'aria-level': 2 } : null)}
          {...(labelId ? { id: labelId } : null)}
        >
          {resolvedHeader}
        </YStack>
      ) : null}
      {fit ? (
        <YStack>{children}</YStack>
      ) : (
        <YStack {...FILL_AND_SCROLL}>
          <ScrollView
            showsVerticalScrollIndicator={false}
            // Net-zero horizontal shift: the negative margin pulls the scroll
            // viewport out by FOCUS_RING_GUTTER, the contentContainer padding
            // pushes the content back in. Edge-aligned controls keep their
            // position but their focus ring no longer hits the overflow-x clip.
            style={{ marginHorizontal: -FOCUS_RING_GUTTER }}
            contentContainerStyle={{
              flexGrow: 1,
              paddingHorizontal: FOCUS_RING_GUTTER,
            }}
          >
            {children}
          </ScrollView>
        </YStack>
      )}
      {footer ? <YStack>{footer}</YStack> : null}
    </YStack>
  );
}

// ---------- Sheet (phone) ----------

function SheetPresentation({
  open,
  onOpenChange,
  snapPoints,
  snapPointsMode,
  disableDragDismiss,
  labelId,
  children,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  snapPoints: number[];
  snapPointsMode: 'percent' | 'fit';
  disableDragDismiss: boolean;
  labelId: string | undefined;
  children: React.ReactNode;
}) {
  const handleOpenChange = (next: boolean) => {
    if (!next) Keyboard.dismiss();
    onOpenChange(next);
  };
  // Focus trap + dialog ARIA per ADR-0077 — shared with BaseSheet,
  // ModalPresentation, and SidebarPresentation via useOverlayA11y.
  const overlay = useOverlayA11y({ open, labelId });
  // In fit mode the sheet sizes to its content — passing percent
  // snapPoints alongside would have Tamagui compete with itself. Strip
  // them so only the fit measurement drives the snap.
  const fit = snapPointsMode === 'fit';
  return (
    <Sheet
      modal
      open={open}
      onOpenChange={handleOpenChange}
      {...(fit
        ? { snapPointsMode: 'fit' as const }
        : { snapPoints, snapPointsMode: 'percent' as const })}
      // When drag-dismiss is disabled, the sheet won't dismiss itself if it
      // ever lands on the bottom snap point — useful for editor sheets
      // where keyboard-induced layout shifts could otherwise close it.
      dismissOnSnapToBottom={!disableDragDismiss}
      disableDrag={disableDragDismiss}
      transition="quick"
    >
      <Sheet.Overlay
        transition="lazy"
        backgroundColor={colors.overlayScrim}
        enterStyle={{ opacity: 0 }}
        exitStyle={{ opacity: 0 }}
      />
      {!disableDragDismiss ? <Sheet.Handle /> : null}
      <Sheet.Frame
        {...({ padding: '$4', ref: overlay.ref, ...overlay.a11yProps } as Record<string, unknown>)}
      >
        {children}
      </Sheet.Frame>
    </Sheet>
  );
}

// ---------- Modal (desktop) ----------

function ModalPresentation({
  open,
  onOpenChange,
  labelId,
  children,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  labelId: string | undefined;
  children: React.ReactNode;
}) {
  const themeColors = useThemeColors();
  const themeName = useThemeName();
  // Esc is handled centrally in AdaptivePanel via useEscapeToClose.
  // Focus trap + dialog ARIA share with the other presentations via
  // useOverlayA11y per ADR-0077.
  const overlay = useOverlayA11y({ open, labelId });

  const handleBackdropPress = () => {
    // Drop the soft keyboard before dismissing. Without this, iOS Safari
    // would close the modal while the keyboard stayed pinned to the
    // bottom of the viewport, leaving a phantom strip of blank space.
    Keyboard.dismiss();
    onOpenChange(false);
  };

  // Only mount the content subtree while open. React Native Web's `Modal`
  // toggles its wrapper to `display: none; aria-hidden="true"` when
  // `visible={false}` but keeps children in the DOM — so a control that
  // still holds focus at close time sits inside an aria-hidden ancestor,
  // which the browser blocks ("Blocked aria-hidden ... descendant retained
  // focus"). Unmounting the children on close drops the focused node, focus
  // falls back to the body, and the focus trap restores it to the trigger.
  return (
    <Modal visible={open} transparent animationType="fade" onRequestClose={() => onOpenChange(false)}>
      {open ? (
        <Theme name={themeName}>
          <Pressable
            style={modalStyles.backdrop}
            onPress={handleBackdropPress}
            accessibilityLabel="Close"
            accessibilityRole="button"
          >
            <Pressable
              onPress={(e) => e.stopPropagation()}
              style={[modalStyles.container, { backgroundColor: themeColors.surface }]}
              accessibilityViewIsModal
              ref={overlay.ref as never}
              {...(overlay.a11yProps as Record<string, unknown>)}
            >
              {children}
            </Pressable>
          </Pressable>
        </Theme>
      ) : null}
    </Modal>
  );
}

// StyleSheet: RN Modal requires style props
const modalStyles = StyleSheet.create({
  backdrop: {
    flex: 1,
    backgroundColor: colors.overlayScrim,
    justifyContent: 'center',
    alignItems: 'center',
  },
  container: {
    width: '90%',
    maxWidth: MODAL_MAX_WIDTH,
    maxHeight: '85%',
    borderRadius: 12,
    padding: 16,
    ...dropShadow({ offsetY: 8, radius: 24, opacity: 0.15, color: colors.shadowDefault, elevation: 10 }),
  },
});

// ---------- Sidebar (desktop) ----------

function SidebarPresentation({
  open,
  onOpenChange,
  isLg,
  labelId,
  children,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  isLg: boolean;
  labelId: string | undefined;
  children: React.ReactNode;
}) {
  const insets = useSafeAreaInsets();
  const themeColors = useThemeColors();
  const themeName = useThemeName();
  const width = isLg ? SIDEBAR_WIDTH_LG : SIDEBAR_WIDTH;
  const [slideAnim] = useState(() => new Animated.Value(width));
  const [mounted, setMounted] = useState(false);
  // Focus trap + dialog ARIA — same shared overlay surface as the
  // other presentations. Enabled gates on `mounted` so the trap waits
  // for the slide-in animation to start.
  const overlay = useOverlayA11y({ open: open && mounted, labelId });

  useEffect(() => {
    // `useNativeDriver: true` is unsupported on web (no RCTAnimation) and
    // logs a warning; fall back to JS-driven on web — the 200ms slide is
    // cheap enough that the perf difference doesn't matter there.
    const useNative = Platform.OS !== 'web';
    if (open) {
      setMounted(true);
      Animated.timing(slideAnim, { toValue: 0, duration: 200, useNativeDriver: useNative }).start();
    } else {
      Animated.timing(slideAnim, { toValue: width, duration: 200, useNativeDriver: useNative }).start(() => setMounted(false));
    }
  }, [open, slideAnim, width]);

  if (!mounted) return null;

  // Portal escape — without this, the absolute-positioned sidebar lands
  // inside the nearest positioned ancestor (e.g. a day card with
  // `position: relative` or a `transform`) instead of the viewport. Sheet
  // (phone) and Modal (modal mode) already portal automatically; only this
  // path needed an explicit one.
  return (
    <Portal>
      <Theme name={themeName}>
        <View style={[StyleSheet.absoluteFill, { zIndex: 100 }]} pointerEvents="box-none">
          <Pressable style={sidebarStyles.backdrop} onPress={() => onOpenChange(false)} />
          <Animated.View
            ref={overlay.ref as never}
            // eslint-disable-next-line @typescript-eslint/no-explicit-any
            {...(overlay.a11yProps as any)}
            style={[
              sidebarStyles.panel,
              {
                width,
                paddingTop: insets.top + 8,
                paddingBottom: insets.bottom + 8,
                transform: [{ translateX: slideAnim }],
                backgroundColor: themeColors.surface,
                borderLeftWidth: StyleSheet.hairlineWidth,
                borderLeftColor: themeColors.borderLight,
              },
            ]}
          >
            <YStack flex={1} minHeight={0} padding="$4">
              {children}
            </YStack>
          </Animated.View>
        </View>
      </Theme>
    </Portal>
  );
}

const sidebarStyles = StyleSheet.create({
  backdrop: { ...StyleSheet.absoluteFill, backgroundColor: colors.overlayScrim },
  panel: {
    position: 'absolute',
    top: 0,
    right: 0,
    bottom: 0,
    overflow: 'hidden',
    ...dropShadow({ offsetX: -2, offsetY: 0, radius: 8, opacity: 0.15, color: colors.shadowDefault, elevation: 8 }),
  },
});
